/**
 * DashboardStatsRow - Slot: the 3 stat cards (Guthaben, Bilder, Produkte).
 *
 * Part of the unified 2/3-1/3 page grid (G2): this row only renders the
 * stat cards side-by-side. The plan card now lives in the right column,
 * composed in Dashboard.tsx (no longer here).
 */

import { Images, Wallet2, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/lib/utils';
import { Sparkline } from '@/components/v2/ui';
import { EID } from '@/lib/element-ids.registry';
import type { DashboardStats } from '@/types';
import type { DashboardMetrics } from '@/lib/api/dashboard-metrics';
import { DashboardStatsCard } from './DashboardStatsCard';

interface DashboardStatsRowProps {
  stats: DashboardStats;
  metrics: DashboardMetrics | undefined;
  canManageCredits: boolean;
}

export function DashboardStatsRow({ stats, metrics, canManageCredits }: DashboardStatsRowProps) {
  const { t, i18n } = useTranslation();

  const toSpark = (s?: { date: string; y: number }[]) =>
    (s ?? []).map((p) => ({ x: p.date, y: p.y }));
  const deltaTrend = (n: number | undefined, context: string) =>
    n === undefined
      ? undefined
      : {
          value: `${n >= 0 ? '+' : '-'}${Math.abs(n).toLocaleString(i18n.language)} ${context}`,
          positive: n >= 0,
        };

  return (
    <div
      className={`grid gap-3 ${
        canManageCredits ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'
      }`}
    >
      {canManageCredits && (
        <DashboardStatsCard
          id={EID['dashboard-stats-credits-region']}
          label={t('billing.availableBalance')}
          value={formatNumber(stats.credits)}
          suffix={t('dashboardStats.creditsSuffix')}
          icon={Wallet2}
          iconColor="text-emerald-400"
          iconBgColor="bg-emerald-950/80 border border-emerald-900/30"
          trend={deltaTrend(metrics?.credits.delta_7d, t('dashboardStats.thisWeek'))}
        >
          <Sparkline data={toSpark(metrics?.credits.series)} />
        </DashboardStatsCard>
      )}
      <DashboardStatsCard
        id={EID['dashboard-stats-images-region']}
        label={t('readiness.generatedImages')}
        value={stats.total_images}
        suffix={t('dashboardStats.imagesSuffix')}
        icon={Images}
        iconColor="text-primary-400"
        iconBgColor="bg-primary-950/80 border border-primary-900/30"
        trend={deltaTrend(metrics?.images.delta_7d, t('dashboardStats.thisWeek'))}
      >
        <Sparkline data={toSpark(metrics?.images.series)} />
      </DashboardStatsCard>
      <DashboardStatsCard
        id={EID['dashboard-stats-products-region']}
        label={t('dashboard.products')}
        value={stats.product_count ?? 0}
        suffix={t('dashboardStats.productsSuffix')}
        icon={Package}
        iconColor="text-primary-400"
        iconBgColor="bg-primary-950/80 border border-primary-900/30"
        trend={deltaTrend(metrics?.products.delta_7d, t('dashboardStats.imported'))}
      >
        <Sparkline data={toSpark(metrics?.products.series)} />
      </DashboardStatsCard>
    </div>
  );
}
